'use client'

import {
  createContext,
  useCallback,
  useContext,
  useReducer,
  useRef,
  type ReactNode,
  useEffect,
  useMemo,
} from 'react'
import api from '@/api'
import { setApiAuthToken } from '@/api'
import { extService } from '@/config/extension.ts'
import { supabase } from '@/services/supabase'
import { joinOrganization } from '@/api/apis/org/invite'
import { toast } from 'sonner'
import config from '@/config/config.env'
import posthog from 'posthog-js'

export function getCookieDomain() {
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname.match(/^\d+\.\d+\.\d+\.\d+$/)) {
    return '';
  }
  const parts = hostname.split('.');
  if (parts.length > 2) {
    return `.${parts.slice(-2).join('.')}`;
  }
  return `.${hostname}`;
}

function getSecureAttr() {
  return typeof window !== 'undefined' && window.location.protocol === 'https:' ? '; Secure' : '';
}

export function clearCookie(name: string) {
  const domain = getCookieDomain();
  const domainAttr = domain ? `; domain=${domain}` : '';
  const secureAttr = getSecureAttr();
  document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT${domainAttr}; SameSite=Lax${secureAttr};`;
  document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax${secureAttr};`;
}

export function syncAuthCookies(session?: any, profile?: any, org?: any) {
  try {
    clearCookie('xecubes_logout_event');
    const domain = getCookieDomain();
    const domainAttr = domain ? `; domain=${domain}` : '';
    const secureAttr = getSecureAttr();

    if (session?.access_token) {
      const ssoData = {
        access_token: session.access_token,
        refresh_token: session.refresh_token,
      };
      document.cookie = `xecubes_sso_session=${encodeURIComponent(JSON.stringify(ssoData))}; path=/; max-age=604800; SameSite=Lax${domainAttr}${secureAttr};`;
    }

    let existingUser: any = {};
    try {
      const match = document.cookie.match(/(^|;\s*)xecubes_user_session=([^;]*)/);
      if (match) existingUser = JSON.parse(decodeURIComponent(match[2]));
    } catch (_) {}

    const email = profile?.email || session?.user?.email || existingUser?.email;
    if (!email) return;

    const toTitleCase = (s: string) => s ? s.trim().split(/\s+/).map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ') : '';
    
    // SaaS profile is the primary source of truth!
    const isGoogleDefaultAvatar = (url?: string) => {
      if (!url || typeof url !== 'string') return false;
      return url.includes('googleusercontent.com');
    };

    let rawAvatar = profile?.avatar || profile?.profile_image || '';
    if (!rawAvatar && existingUser?.avatar_url && !isGoogleDefaultAvatar(existingUser.avatar_url)) {
      rawAvatar = existingUser.avatar_url;
    }
    const avatarUrl = isGoogleDefaultAvatar(rawAvatar) ? '' : rawAvatar;

    const rawFirstName = profile?.first_name || (existingUser?.first_name !== undefined ? existingUser.first_name : (session?.user?.user_metadata?.first_name || ''));
    const rawLastName = profile?.last_name || (existingUser?.last_name !== undefined ? existingUser.last_name : (session?.user?.user_metadata?.last_name || ''));
    
    let rawName = '';
    if (profile?.first_name) {
      rawName = `${profile.first_name} ${profile.last_name || ''}`.trim();
    } else if (rawFirstName || rawLastName) {
      rawName = `${rawFirstName} ${rawLastName}`.trim();
    } else if (profile?.name) {
      rawName = profile.name;
    } else if (existingUser?.name) {
      rawName = existingUser.name;
    } else if (session?.user?.user_metadata?.full_name) {
      rawName = session.user.user_metadata.full_name;
    } else {
      rawName = email.split('@')[0];
    }

    const firstName = toTitleCase(rawFirstName);
    const lastName = toTitleCase(rawLastName);
    const name = toTitleCase(rawName);

    // Organization details from SaaS
    const orgName = org?.name || existingUser?.org_name || '';
    const orgRole = org?.role || existingUser?.org_role || '';
    const orgLogo = org?.logo || existingUser?.org_logo || '';

    const userSession = {
      id: profile?.user_id || session?.user?.id || existingUser?.id,
      email,
      name,
      first_name: firstName,
      last_name: lastName,
      avatar_url: avatarUrl,
      org_name: orgName,
      org_role: orgRole,
      org_logo: orgLogo,
      portal: 'recruiter',
      portalUrl: window.location.origin,
      updatedAt: Date.now(),
    };

    document.cookie = `xecubes_user_session=${encodeURIComponent(JSON.stringify(userSession))}; path=/; max-age=604800; SameSite=Lax${domainAttr}${secureAttr};`;
    document.cookie = `xecubes_sso_active=true; path=/; max-age=604800; SameSite=Lax${domainAttr}${secureAttr};`;
    document.cookie = `xecubes_active_portal=${encodeURIComponent(window.location.origin)}; path=/; max-age=604800; SameSite=Lax${domainAttr}${secureAttr};`;

    // Broadcast login to all tabs (marketing website, other portals)
    try {
      const channel = new BroadcastChannel('xecubes_auth_channel');
      channel.postMessage({ type: 'LOGIN', user: userSession, timestamp: Date.now() });
      channel.close();
    } catch (_) {}
  } catch (err) {
    console.warn('[AUTH] Error syncing auth cookies:', err);
  }
}

export function clearAllAuthCookies() {
  clearCookie('xecubes_sso_session');
  clearCookie('xecubes_user_session');
  clearCookie('xecubes_sso_active');
  clearCookie('xecubes_active_portal');

  // Set explicit logout event cookie (tells active background tabs to reject stale sessions)
  const domain = getCookieDomain();
  const domainAttr = domain ? `; domain=${domain}` : '';
  const secureAttr = getSecureAttr();
  document.cookie = `xecubes_logout_event=${Date.now()}; path=/; max-age=5; SameSite=Lax${domainAttr}${secureAttr};`;
  document.cookie = `xecubes_logout_event=${Date.now()}; path=/; max-age=5; SameSite=Lax${secureAttr};`;

  // Broadcast logout to all tabs immediately
  try {
    const channel = new BroadcastChannel('xecubes_auth_channel');
    channel.postMessage({ type: 'LOGOUT', timestamp: Date.now() });
    channel.close();
  } catch (_) {}
}

interface Timestamp {
  _seconds: number
  _nanoseconds: number
}
interface CurrentUser {
  first_name: string | null
  last_name: string | null
  email: string
  is_email_verified: boolean
  updated_at: Timestamp
  created_at: Timestamp
  user_id: string
  default_org: string | null
  profile_completed: boolean
  provider_id: string
  source: string
  phone: string | null
}

interface AuthContextType {
  user: CurrentUser | null
  login: (credentials: LoginCredentials) => Promise<LoginResult & { mfaRequired?: boolean }>
  verifyMfa: (code: string, turnstile_token?: string) => Promise<LoginResult>
  logout: (
    notify_user?: boolean,
    reload?: boolean
  ) => Promise<{ error?: string }>
  loading: boolean
  fetchUserProfile: (uid?: string) => Promise<void>
  isLoggedIn: boolean
  loginWithToken: (
    org_id: string | null,
    turnstile_token?: string
  ) => Promise<{ error?: string }>
  logInWithCustomToken: (token: string) => Promise<{ error?: string } | true>
  customAuthLoading: boolean
  signInWithProvider: () => Promise<
    { success?: boolean; profileCompleted?: boolean } | undefined
  >
  isSubscriptionActive: boolean
  token: string | null
  refreshActiveSession: () => Promise<void>
  isImpersonated: boolean
  impersonationLogId: string | null
  startImpersonating: (targetUserId: string, reason: string) => Promise<void>
  stopImpersonating: () => Promise<void>
  bootstrapData: any | null
}

interface LoginCredentials {
  email: string
  password: string
  turnstile_token?: string
}

interface LoginResult {
  success?: boolean
  error?: string
}

// Reducer State & Actions
interface AuthState {
  user: CurrentUser | null
  isLoggedIn: boolean
  loading: boolean
  customAuthLoading: boolean
  token: string | null
  isSubscriptionActive: boolean
  isImpersonated: boolean
  impersonationLogId: string | null
  bootstrapData: any | null
}

type AuthAction =
  | { type: 'START_BOOTSTRAP' }
  | { type: 'BOOTSTRAP_SUCCESS'; payload: { token: string | null; user: CurrentUser | null; isSubscriptionActive: boolean; bootstrapData: any } }
  | { type: 'BOOTSTRAP_FAILURE' }
  | { type: 'SET_CUSTOM_LOADING'; payload: boolean }
  | { type: 'SET_TOKEN'; payload: string | null }
  | { type: 'UPDATE_USER'; payload: CurrentUser | null }
  | { type: 'SET_IMPERSONATION'; payload: { isImpersonated: boolean; impersonationLogId: string | null } }
  | { type: 'LOGOUT_COMPLETE' }

function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'START_BOOTSTRAP':
      return {
        ...state,
        loading: state.user ? false : true,
      }
    case 'BOOTSTRAP_SUCCESS':
      return {
        ...state,
        loading: false,
        token: action.payload.token,
        user: action.payload.user,
        isLoggedIn: !!action.payload.user,
        isSubscriptionActive: action.payload.isSubscriptionActive,
        bootstrapData: action.payload.bootstrapData,
      }
    case 'BOOTSTRAP_FAILURE':
      return {
        ...state,
        loading: false,
        isLoggedIn: false,
        user: null,
        token: null,
        bootstrapData: null,
      }
    case 'SET_CUSTOM_LOADING':
      return {
        ...state,
        customAuthLoading: action.payload,
      }
    case 'SET_TOKEN':
      return {
        ...state,
        token: action.payload,
      }
    case 'UPDATE_USER':
      return {
        ...state,
        user: action.payload,
        isLoggedIn: !!action.payload,
      }
    case 'SET_IMPERSONATION':
      return {
        ...state,
        isImpersonated: action.payload.isImpersonated,
        impersonationLogId: action.payload.impersonationLogId,
      }
    case 'LOGOUT_COMPLETE':
      return {
        ...state,
        user: null,
        isLoggedIn: false,
        loading: false,
        token: null,
        isSubscriptionActive: false,
        bootstrapData: null,
      }
    default:
      return state
  }
}

const initialState: AuthState = {
  user: null,
  isLoggedIn: false,
  loading: true,
  customAuthLoading: false,
  token: null,
  isSubscriptionActive: false,
  isImpersonated: false,
  impersonationLogId: null,
  bootstrapData: null,
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(authReducer, initialState)
  const bootstrapInFlight = useRef(false)
  const lastBootstrappedToken = useRef<string | null>(null)
  
  // Track current state in a ref to prevent stale closures in the useEffect callback
  const stateRef = useRef(state)
  stateRef.current = state

  // Consolidated Bootstrap Flow
  const triggerBootstrap = useCallback(async (accessToken: string | null) => {
    if (bootstrapInFlight.current) return
    bootstrapInFlight.current = true

    lastBootstrappedToken.current = accessToken
    dispatch({ type: 'START_BOOTSTRAP' })

    try {
      if (!accessToken) {
        dispatch({ type: 'BOOTSTRAP_FAILURE' })
        bootstrapInFlight.current = false
        return
      }

      setApiAuthToken(accessToken)

      // Fetch consolidative bootstrap data
      const response = await api.get('/recruiter/bootstrap')
      const bootstrapPayload = response.data.data

      dispatch({
        type: 'BOOTSTRAP_SUCCESS',
        payload: {
          token: accessToken,
          user: bootstrapPayload.profile,
          isSubscriptionActive: bootstrapPayload.subscription?.active || false,
          bootstrapData: bootstrapPayload,
        },
      })

      // Identify user in PostHog on successful bootstrap
      if (bootstrapPayload.profile) {
        posthog.identify(bootstrapPayload.profile.user_id, {
          email: bootstrapPayload.profile.email,
          first_name: bootstrapPayload.profile.first_name,
          last_name: bootstrapPayload.profile.last_name,
          portal: 'recruiter',
        })
        syncAuthCookies(null, bootstrapPayload.profile, bootstrapPayload.current_organization)
      }

      if (bootstrapPayload.organizations?.default_id) {
        posthog.group('organization', bootstrapPayload.organizations.default_id, {
          name: bootstrapPayload.organizations.list.find(o => o.id === bootstrapPayload.organizations.default_id)?.name || 'Unknown',
        })
      }

      // Update extension session
      let refreshToken = ''
      try {
        const { data: { session } } = await supabase.auth.getSession()
        if (session?.refresh_token) {
          refreshToken = session.refresh_token
        }
      } catch (_) {}

      extService({
        token: accessToken,
        refreshToken: refreshToken,
        type: 'AUTH_TOKEN',
      }).catch((e) => console.error('[AUTH] Extension update failed:', e))

    } catch (err) {
      console.error('[AUTH] Bootstrap sequence failed:', err)
      dispatch({ type: 'BOOTSTRAP_FAILURE' })
    } finally {
      bootstrapInFlight.current = false
    }
  }, [])

  // Listen for Supabase auth state changes
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (import.meta.env.MODE === 'development') {
        console.log('[AUTH] onAuthStateChange event:', event, session?.user?.email)
      }

      if (!session) {
        clearCookie('xecubes_sso_session');
        clearCookie('xecubes_user_session');
        clearCookie('xecubes_sso_active');
        clearCookie('xecubes_active_portal');
        setApiAuthToken(null);
        dispatch({ type: 'LOGOUT_COMPLETE' });
        posthog.reset(); // Reset PostHog session tracking on logout
        return;
      }

      // Safe SSO cookie and session sync
      syncAuthCookies(session)

      // If the token is the same and we already have a user, ignore the redundant event
      // This happens on tab focus / window focus and previously triggered full-page reloads
      if (session.access_token === lastBootstrappedToken.current && stateRef.current.user) {
        return
      }

      // Always sync token to Axios and state to avoid 401s on subsequent handover calls
      setApiAuthToken(session.access_token)
      dispatch({ type: 'SET_TOKEN', payload: session.access_token })

      if (window.location.pathname.includes('/oneauth/')) {
        return
      }

      triggerBootstrap(session.access_token)
    })

    // Set initial session synchronously from memory/localStorage
    supabase.auth.getSession().then(({ data: { session } }) => {
      // If we are currently completing a oneauth handover, never block or purge!
      if (window.location.pathname.includes('/oneauth/')) {
        clearCookie('xecubes_logout_event');
        return;
      }

      const hasLogoutEvent = document.cookie.split(';').some(c => 
        c.trim().startsWith('xecubes_logout_event=')
      );

      // Block ghost session re-hydration ONLY if an explicit logout event was triggered
      if (hasLogoutEvent) {
        console.log('[AUTH] Ghost session detected after suite logout. Purging...');
        Object.keys(localStorage).forEach((key) => {
          if (key.startsWith('sb-') && key.endsWith('-auth-token')) {
            localStorage.removeItem(key);
          }
        });
        localStorage.removeItem('auth_backend_synced');
        dispatch({ type: 'BOOTSTRAP_FAILURE' });
        supabase.auth.signOut({ scope: 'global' }).catch(() => {});
        return;
      }

      if (session) {
        setApiAuthToken(session.access_token);
        dispatch({ type: 'SET_TOKEN', payload: session.access_token });
        syncAuthCookies(session);
        triggerBootstrap(session.access_token);
      } else {
        dispatch({ type: 'BOOTSTRAP_FAILURE' });
      }
    })

    // Cross-tab broadcast listener for immediate remote logout
    let authChannel: BroadcastChannel | null = null;
    try {
      authChannel = new BroadcastChannel('xecubes_auth_channel');
      authChannel.onmessage = async (event) => {
        if (event.data?.type === 'LOGOUT') {
          // Ignore remote logout if this tab is actively executing a login handover
          if (window.location.pathname.includes('/oneauth/')) {
            return;
          }
          console.log('[AUTH] Remote logout received via BroadcastChannel');
          clearCookie('xecubes_sso_session');
          clearCookie('xecubes_user_session');
          clearCookie('xecubes_sso_active');
          clearCookie('xecubes_active_portal');
          Object.keys(localStorage).forEach((key) => {
            if (key.startsWith('sb-') && key.endsWith('-auth-token')) {
              localStorage.removeItem(key);
            }
          });
          localStorage.removeItem('auth_backend_synced');
          dispatch({ type: 'LOGOUT_COMPLETE' });
          try {
            await supabase.auth.signOut({ scope: 'global' });
          } catch (_) {}
          // Immediately redirect to auth sign-in page so this tab doesn't stay on dashboard
          window.location.replace(`${config.urls.authUrl}/sign-in?portal=recruiter`);
        }
      };
    } catch (_) {}

    return () => {
      subscription.unsubscribe()
      if (authChannel) authChannel.close()
    }
  }, [triggerBootstrap])

  // Login credentials authentication
  const login = async ({
    email,
    password,
    turnstile_token,
  }: LoginCredentials): Promise<LoginResult & { mfaRequired?: boolean }> => {
    dispatch({ type: 'SET_CUSTOM_LOADING', payload: true })
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      })
      if (error) throw error

      // MFA check
      const { data: mfaData } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel()
      if (mfaData && mfaData.nextLevel === 'aal2' && mfaData.currentLevel !== 'aal2') {
        dispatch({ type: 'SET_CUSTOM_LOADING', payload: false })
        return { mfaRequired: true, success: false }
      }

      // Bootstrap will be triggered by onAuthStateChange event automatically.
      return { success: true }
    } catch (error: any) {
      api.post('/recruiter/auth/log-failure', { email }).catch(e => {
        console.error('[AUTH] Failed to register login failure:', e)
      })

      const message = error.message || 'Invalid credentials'
      toast.error(message)
      dispatch({ type: 'SET_CUSTOM_LOADING', payload: false })
      return { error: message }
    }
  }

  const verifyMfa = async (
    code: string,
    turnstile_token?: string
  ): Promise<LoginResult> => {
    dispatch({ type: 'SET_CUSTOM_LOADING', payload: true })
    try {
      const { data: factorsData, error: factorsError } = await supabase.auth.mfa.listFactors()
      if (factorsError) throw factorsError

      const verifiedFactors = factorsData.totp.filter(f => f.status === 'verified')
      if (verifiedFactors.length === 0) {
        throw new Error('No verified MFA factors found.')
      }

      const factorId = verifiedFactors[0].id

      const { data: challenge, error: challengeError } = await supabase.auth.mfa.challenge({ factorId })
      if (challengeError) throw challengeError

      const { error: verifyError } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: challenge.id,
        code,
      })
      if (verifyError) throw verifyError

      return { success: true }
    } catch (error: any) {
      toast.error(error.message || 'MFA verification failed')
      dispatch({ type: 'SET_CUSTOM_LOADING', payload: false })
      return { error: error.message || 'MFA verification failed' }
    }
  }

  // Backward compatible loginWithToken to handle organization updates
  const loginWithToken = async (
    org_id: string | null,
    _turnstile_token?: string
  ): Promise<{ error?: string }> => {
    try {
      const response = await api.post(`/recruiter/auth/token`, {
        org_id: org_id || null,
      })

      let activeToken = response.data.data.token
      if (activeToken) {
        try {
          const payload = JSON.parse(atob(activeToken.split('.')[1]))
          const tokenOrgId = payload.app_metadata?.org_id || payload.org_id || null
          const targetOrgId = response.data.data.org_id || null

          if (targetOrgId && tokenOrgId !== targetOrgId) {
            console.log('[AUTH] Organization mismatch in token. Refreshing session...')
            const { data: refreshData } = await supabase.auth.refreshSession()
            if (refreshData?.session) {
              activeToken = refreshData.session.access_token
            }
          }
        } catch (e) {
          console.warn('[AUTH] Token claim sync check failed:', e)
        }

        setApiAuthToken(activeToken)
        await triggerBootstrap(activeToken)
      }

      return {}
    } catch (e: any) {
      const error = e?.response?.data?.message || 'Token handshake failed';
      console.warn('[AUTH] Token handshake / organization sync error:', error);
      return {
        error: String(error).toLowerCase().includes('not found')
          ? `user is not registered! Please Signup.`
          : 'Oops! Something went wrong on our end. Please try again later.',
      }
    }
  }

  const logout = async (
    notify_user = true,
    reload = true
  ): Promise<{ error?: string }> => {
    dispatch({ type: 'START_BOOTSTRAP' })
    try {
      try {
        await api.post('/recruiter/auth/sign-out').catch(() => {})
      } catch (_) {}

      try {
        await supabase.auth.signOut({ scope: 'global' })
      } catch (_) {}

      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('sb-') && key.endsWith('-auth-token')) {
          localStorage.removeItem(key)
        }
      })
      localStorage.removeItem('auth_backend_synced')

      clearAllAuthCookies()

      dispatch({ type: 'LOGOUT_COMPLETE' })
      window.indexedDB.deleteDatabase('recruiter-tags-db')

      if (notify_user) toast.success('Logged out')

      await extService({
        type: 'LOGOUT',
      }).catch(() => {})

      return {}
    } catch (error: any) {
      console.error('Error during sign out', error)
      return { error: error.message }
    } finally {
      if (reload) {
        window.location.replace(`${config.urls.authUrl}/sign-out?portal=recruiter`)
      }
    }
  }

  const fetchUserProfile = async (): Promise<void> => {
    const { data: { session } } = await supabase.auth.getSession()
    if (session) {
      await triggerBootstrap(session.access_token)
    }
  }

  const logInWithCustomToken = async (
    token: string
  ): Promise<{ error?: string } | true> => {
    try {
      let refreshToken = ''
      try {
        const { data: { session: existingSession } } = await supabase.auth.getSession()
        if (existingSession?.refresh_token) {
          refreshToken = existingSession.refresh_token
        }
      } catch (_) {}

      const { error } = await supabase.auth.setSession({
        access_token: token,
        refresh_token: refreshToken,
      })
      if (error) return { error: error.message }
      return true
    } catch (error: any) {
      return { error: error.message }
    }
  }

  const signInWithProvider = async (): Promise<
    { success?: boolean; profileCompleted?: boolean } | undefined
  > => {
    dispatch({ type: 'SET_CUSTOM_LOADING', payload: true })
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: window.location.origin + '/callback/google-oauth'
        }
      })
      if (error) throw error
      return { success: true }
    } catch (_) {
      toast.error('Authentication failed. Please try again.')
    } finally {
      dispatch({ type: 'SET_CUSTOM_LOADING', payload: false })
    }
  }

  const refreshActiveSession = async (): Promise<void> => {
    try {
      const { data, error } = await supabase.auth.refreshSession()
      if (error) throw error
      if (data.session) {
        setApiAuthToken(data.session.access_token)
        await triggerBootstrap(data.session.access_token)
      }
    } catch (err: any) {
      console.error('[AUTH] Failed to refresh active session:', err.message)
    }
  }

  useEffect(() => {
    if (state.token) {
      try {
        const payload = JSON.parse(atob(state.token.split('.')[1]))
        dispatch({
          type: 'SET_IMPERSONATION',
          payload: {
            isImpersonated: !!payload.is_impersonated,
            impersonationLogId: payload.impersonation_log_id || null,
          },
        })
      } catch (e) {
        dispatch({
          type: 'SET_IMPERSONATION',
          payload: { isImpersonated: false, impersonationLogId: null },
        })
      }
    } else {
      dispatch({
        type: 'SET_IMPERSONATION',
        payload: { isImpersonated: false, impersonationLogId: null },
      })
    }
  }, [state.token])

  useEffect(() => {
    // AUTO-JOIN HOOK: If we are logged in and have a pending invite in sessionStorage, accept it automatically.
    const autoJoin = async () => {
      const pendingInviteId = sessionStorage.getItem('pending_invite_id')
      if (state.isLoggedIn && pendingInviteId && !state.loading) {
        try {
          console.debug(`[AUTH] Auto-joining invitation: ${pendingInviteId}`)
          await joinOrganization(pendingInviteId)
          
          // Clear intent after success
          sessionStorage.removeItem('pending_invite_id')
          toast.success(`Welcome aboard! You have joined the organization.`)
          
          // Refresh session to pick up the new metadata claims from backend
          await supabase.auth.refreshSession()
          
          // Redirect to dashboard
          window.location.replace('/')
        } catch (err: any) {
          const isDuplicate = err.response?.status === 409 || err.message?.includes('409');
          console.error('[AUTH] Auto-join status check:', err.response?.data?.message || err.message);
          
          sessionStorage.removeItem('pending_invite_id');

          if (isDuplicate) {
            console.log('[AUTH] User is already a member of the organization. Refreshing session to sync claims.');
            toast.success(`Welcome aboard! You have joined the organization.`);
            await supabase.auth.refreshSession();
            window.location.replace('/');
          }
        }
      }
    }
    
    autoJoin()
  }, [state.isLoggedIn, state.loading])

  const startImpersonating = async (targetUserId: string, reason: string) => {
    try {
      const res = await api.post('/recruiter/enterprise/impersonate/start', {
        target_user_id: targetUserId,
        reason
      })
      if (res.data?.data?.token) {
        if (state.token) {
          localStorage.setItem('original_access_token', state.token)
        }
        await logInWithCustomToken(res.data.data.token)
        toast.success(`You are now impersonating ${res.data.data.user.email}`)
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to start impersonation.')
      throw err
    }
  }

  const stopImpersonating = async () => {
    try {
      if (state.impersonationLogId) {
        await api.post('/recruiter/enterprise/impersonate/stop', {
          log_id: state.impersonationLogId
        })
      }

      const originalToken = localStorage.getItem('original_access_token')
      if (originalToken) {
        await logInWithCustomToken(originalToken)
        localStorage.removeItem('original_access_token')
        toast.success('Impersonation terminated. Restored your admin session.')
      } else {
        await logout(true, true)
      }
    } catch (err: any) {
      toast.error('Failed to terminate impersonation cleanly.')
      await logout(true, true)
    }
  }

  const contextValue = useMemo<AuthContextType>(() => ({
    user: state.user,
    login,
    verifyMfa,
    token: state.token,
    logout,
    loading: state.loading,
    fetchUserProfile,
    isLoggedIn: state.isLoggedIn,
    loginWithToken,
    logInWithCustomToken,
    customAuthLoading: state.customAuthLoading,
    signInWithProvider,
    isSubscriptionActive: state.isSubscriptionActive,
    refreshActiveSession,
    isImpersonated: state.isImpersonated,
    impersonationLogId: state.impersonationLogId,
    bootstrapData: state.bootstrapData,
  }), [state])

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
}
